// =============================================================================
// api/admin.ts
// =============================================================================

import { supabase } from "@cl/supabase";
import type {
  AdminUserSummary,
  AdminUserDetail,
  AdminUserTour,
  AdminUserRole,
} from "@/types";
import { handleSupabaseError } from "./_error";
import { deleteAvatar, uploadAvatar } from "./media";
import { buildRoute } from "@u/routes";

export type CreateUserPayload = {
  email: string;
  name: string;
  password: string;
  role: "client" | "admin" | "suspended";
};

export const fetchAdminUsersSummary = async (): Promise<AdminUserSummary[]> => {
  const { data, error } = await supabase.rpc("get_admin_users_summary");
  if (error) handleSupabaseError(error, "fetchAdminUsersSummary");
  return (data ?? []) as AdminUserSummary[];
};

export const setUserRole = async (
  userId: string,
  role: AdminUserRole,
): Promise<void> => {
  const { error } = await supabase.rpc("admin_set_user_role", {
    p_user_id: userId,
    p_role: role,
  });
  if (error) handleSupabaseError(error, "setUserRole");

  if (role === "suspended") {
    // Fire-and-forget — don't block UI on email delivery
    supabase.functions
      .invoke("send-account-suspended", { body: { userId } })
      .catch((err) => {
        console.warn("send-account-suspended failed:", err);
      });
  }
};

export const deleteUser = async (userId: string): Promise<void> => {
  const { error } = await supabase.rpc("delete_user", { target_id: userId });
  if (error) handleSupabaseError(error, "deleteUser");
};

export const transferTourOwner = async (
  tourId: string,
  newOwnerId: string,
): Promise<void> => {
  const { error } = await supabase.rpc("admin_transfer_tour_owner", {
    p_tour_id: tourId,
    p_new_owner_id: newOwnerId,
  });
  if (error) handleSupabaseError(error, "transferTourOwner");
};

export const createUser = async (payload: CreateUserPayload): Promise<void> => {
  const { error } = await supabase.functions.invoke("create-user", {
    body: payload,
  });
  if (error) handleSupabaseError(error, "createUser");
};

export const fetchAdminUserDetail = async (
  userId: string,
): Promise<AdminUserDetail> => {
  const { data, error } = await supabase.rpc("get_admin_user_detail", {
    p_user_id: userId,
  });
  if (error) handleSupabaseError(error, "fetchAdminUserDetail");
  if (!data || (data as AdminUserDetail[]).length === 0)
    throw new Error("User not found");
  return (data as AdminUserDetail[])[0];
};

export const fetchAdminUserTours = async (
  userId: string,
): Promise<AdminUserTour[]> => {
  const { data, error } = await supabase.rpc("get_admin_user_tours", {
    p_user_id: userId,
  });
  if (error) handleSupabaseError(error, "fetchAdminUserTours");
  return (data ?? []) as AdminUserTour[];
};

export const setEarlyAdopter = async (
  userId: string,
  value: boolean,
): Promise<void> => {
  const { error } = await supabase.rpc("admin_set_early_adopter", {
    p_user_id: userId,
    p_value: value,
  });
  if (error) handleSupabaseError(error, "setEarlyAdopter");
};

/** Admin: assign/clear a user's vanity subdomain (null clears). */
export const setSubdomain = async (
  userId: string,
  slug: string | null,
): Promise<void> => {
  const { error } = await supabase.rpc("admin_set_subdomain", {
    p_user_id: userId,
    p_slug: slug,
  });
  if (error) handleSupabaseError(error, "setSubdomain");
};

export interface AdminUpdateProfilePayload {
  name: string | null;
  contact_email: string | null;
  contact_phone: string | null;
  contact_whatsapp: string | null;
}

export const updateUserProfile = async (
  userId: string,
  payload: AdminUpdateProfilePayload,
): Promise<void> => {
  const { error } = await supabase.rpc("admin_update_profile", {
    p_user_id: userId,
    p_name: payload.name,
    p_contact_email: payload.contact_email,
    p_contact_phone: payload.contact_phone,
    p_contact_whatsapp: payload.contact_whatsapp,
  });
  if (error) handleSupabaseError(error, "updateUserProfile");
};

// Set/clear another user's avatar_url (admin-gated RPC bypasses owner-only RLS).
const adminSetAvatarUrl = async (
  userId: string,
  avatarUrl: string | null,
): Promise<void> => {
  const { error } = await supabase.rpc("admin_set_avatar", {
    p_user_id: userId,
    p_avatar_url: avatarUrl,
  });
  if (error) handleSupabaseError(error, "adminSetAvatarUrl");
};

// Upload + set a new avatar for the target user, then best-effort delete the old.
export const adminUpdateAvatar = async (
  userId: string,
  file: File,
  oldUrl?: string | null,
): Promise<string> => {
  const newUrl = await uploadAvatar(userId, file);
  try {
    await adminSetAvatarUrl(userId, newUrl);
  } catch (err) {
    await deleteAvatar(newUrl);
    throw err;
  }
  if (oldUrl) await deleteAvatar(oldUrl);
  return newUrl;
};

export const adminRemoveAvatar = async (
  userId: string,
  oldUrl?: string | null,
): Promise<void> => {
  await adminSetAvatarUrl(userId, null);
  if (oldUrl) await deleteAvatar(oldUrl);
};

export const setUserDemo = async (
  userId: string,
  value: boolean,
): Promise<void> => {
  const { error } = await supabase.rpc("admin_set_user_demo", {
    p_user_id: userId,
    p_value: value,
  });
  if (error) handleSupabaseError(error, "setUserDemo");
};

export interface MockResetSummary {
  tours: number;
  venues: number;
  leads: number;
  bookings: number;
}

// Rebuilds the mock demo client's content (tours/venues/leads/bookings) from the
// live source projects. Admin-only RPC (20260626_mock_reset_demo_data). Used to
// hand a clean demo account to the next sales walkthrough.
export const resetMockData = async (): Promise<MockResetSummary> => {
  const { data, error } = await supabase.rpc("admin_reset_mock_data");
  if (error) handleSupabaseError(error, "resetMockData");
  return (data ?? {
    tours: 0,
    venues: 0,
    leads: 0,
    bookings: 0,
  }) as MockResetSummary;
};

export const sendPasswordReset = async (email: string): Promise<void> => {
  const { error } = await supabase.auth.resetPasswordForEmail(email, {
    redirectTo: `${import.meta.env.VITE_APP_DOMAIN}${buildRoute.resetPassword()}`,
  });
  if (error) handleSupabaseError(error, "sendPasswordReset");
};

// ── Manual plan grants (admin) ─────────────────────────────────────────────────
// Self-serve Stripe checkout is disabled for launch; admins grant plans per
// product after a manual payment. RPCs in 20260621160000_admin_grant_subscriptions.

export type BillingProduct = "360";

export interface ProductSubscriptionSummary {
  plan_type: string;
  status: string;
  current_period_end: string | null;
}

export interface AdminUserSubscriptions {
  "360": ProductSubscriptionSummary | null;
}

export const fetchAdminUserSubscriptions = async (
  userId: string,
): Promise<AdminUserSubscriptions> => {
  const { data, error } = await supabase.rpc("get_admin_user_subscriptions", {
    p_user_id: userId,
  });
  if (error) handleSupabaseError(error, "fetchAdminUserSubscriptions");
  return (data ?? { "360": null }) as AdminUserSubscriptions;
};

export const setUserSubscription = async (
  userId: string,
  product: BillingProduct,
  plan: string,
  periodEnd: string | null,
): Promise<void> => {
  const { error } = await supabase.rpc("admin_set_subscription", {
    p_user_id: userId,
    p_product: product,
    p_plan: plan,
    p_period_end: periodEnd,
  });
  if (error) handleSupabaseError(error, "setUserSubscription");
};

// Change a user's LOGIN (auth) email — distinct from profiles.contact_email.
// Goes through the admin-update-email edge fn (service-role auth.admin call);
// the client never touches auth.users directly.
export const adminUpdateLoginEmail = async (
  userId: string,
  email: string,
): Promise<void> => {
  const { data, error } = await supabase.functions.invoke(
    "admin-update-email",
    { body: { userId, email } },
  );
  if (error) handleSupabaseError(error, "adminUpdateLoginEmail");
  if (!data?.ok) throw new Error(data?.error ?? "email_update_failed");
};

// ── Promo Codes ──────────────────────────────────────────────────────────────

// ── Email Management ─────────────────────────────────────────────────────────

export interface SendAdminEmailPayload {
  to: string; // email address | 'all_users' | 'active_subscribers'
  subject: string;
  html: string;
  type: "manual" | "broadcast";
}

export const sendAdminEmail = async (
  payload: SendAdminEmailPayload,
): Promise<{ sent: boolean; count: number }> => {
  const { data, error } = await supabase.functions.invoke("send-admin-email", {
    body: payload,
  });
  if (error) handleSupabaseError(error, "sendAdminEmail");
  return data as { sent: boolean; count: number };
};
